import { useEffect, useRef, useState } from "react";

interface Floating3DImageProps {
  imageUrl: string;
  highResImageUrl?: string; // Optional high-res image for zoom animation
  className?: string;
  containerClassName?: string;
  maxRotation?: number;
  effectRadius?: number; // Radius around the image where effect is active (in pixels)
}

export const Floating3DImage = ({
  imageUrl,
  highResImageUrl,
  className = "",
  containerClassName = "",
  maxRotation = 15,
  effectRadius = 300,
}: Floating3DImageProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [transform, setTransform] = useState({ rotateX: 0, rotateY: 0 });
  const [isDesktop, setIsDesktop] = useState(false);
  const [zoomProgress, setZoomProgress] = useState(0);
  const [panProgress, setPanProgress] = useState(0);

  useEffect(() => {
    // Check if desktop on mount and resize
    const checkDesktop = () => {
      setIsDesktop(window.innerWidth >= 768);
    };
    checkDesktop();
    window.addEventListener("resize", checkDesktop);

    return () => window.removeEventListener("resize", checkDesktop);
  }, []);

  // Zoom animation loop
  useEffect(() => {
    if (!highResImageUrl) return;

    const animationDuration = 10000; // 10 seconds total cycle
    const zoomInDuration = 3000; // 3 seconds zoom in
    const panDuration = 2000; // 2 seconds pan down to up
    const holdDuration = 2000; // 2 seconds hold
    const zoomOutDuration = 3000; // 3 seconds zoom out

    let startTime: number | null = null;
    let animationFrame: number;

    const animate = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const elapsed = (timestamp - startTime) % animationDuration;

      let progress = 0;
      let panProgress = 0;

      if (elapsed < zoomInDuration) {
        // Zoom in phase: 0 -> 1
        progress = elapsed / zoomInDuration;
        // Ease in-out
        progress =
          progress < 0.5
            ? 2 * progress * progress
            : 1 - Math.pow(-2 * progress + 2, 2) / 2;
      } else if (elapsed < zoomInDuration + panDuration) {
        // Pan phase: move from down to up while zoomed
        progress = 1;
        const panElapsed = elapsed - zoomInDuration;
        panProgress = panElapsed / panDuration;
        // Ease in-out for smooth panning
        panProgress =
          panProgress < 0.5
            ? 2 * panProgress * panProgress
            : 1 - Math.pow(-2 * panProgress + 2, 2) / 2;
      } else if (elapsed < zoomInDuration + panDuration + holdDuration) {
        // Hold phase: stay at 1 with full pan
        progress = 1;
        panProgress = 1;
      } else {
        // Zoom out phase: 1 -> 0
        const zoomOutElapsed =
          elapsed - zoomInDuration - panDuration - holdDuration;
        progress = 1 - zoomOutElapsed / zoomOutDuration;
        panProgress = 1 - zoomOutElapsed / zoomOutDuration;
        // Ease in-out
        progress =
          progress < 0.5
            ? 2 * progress * progress
            : 1 - Math.pow(-2 * progress + 2, 2) / 2;
        panProgress =
          panProgress < 0.5
            ? 2 * panProgress * panProgress
            : 1 - Math.pow(-2 * panProgress + 2, 2) / 2;
      }

      setZoomProgress(progress);
      setPanProgress(panProgress);
      animationFrame = requestAnimationFrame(animate);
    };

    animationFrame = requestAnimationFrame(animate);

    return () => {
      if (animationFrame) cancelAnimationFrame(animationFrame);
    };
  }, [highResImageUrl]);

  useEffect(() => {
    if (!isDesktop || !containerRef.current) return;

    const handleMouseMove = (e: MouseEvent) => {
      const element = containerRef.current;
      if (!element) return;

      const rect = element.getBoundingClientRect();
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;

      // Calculate mouse position relative to center
      const mouseX = e.clientX - centerX;
      const mouseY = e.clientY - centerY;

      // Calculate distance from center
      const distance = Math.sqrt(mouseX * mouseX + mouseY * mouseY);

      // Calculate intensity based on distance
      // 0 at center, increases to 1 at effectRadius/2, then decreases back to 0 at effectRadius
      let intensity = 0;
      if (distance <= effectRadius) {
        // Create a bell curve effect: 0 at center and edges, peak in between
        const normalizedDistance = distance / effectRadius;
        intensity = Math.sin(normalizedDistance * Math.PI); // Sin curve from 0 to 1 to 0
      }

      // Calculate rotation based on mouse position and intensity
      const rotateY = (mouseX / effectRadius) * maxRotation * intensity;
      const rotateX = -(mouseY / effectRadius) * maxRotation * intensity;

      setTransform({ rotateX, rotateY });
    };

    // Add listener to window for wider tracking area
    window.addEventListener("mousemove", handleMouseMove);

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
    };
  }, [isDesktop, maxRotation, effectRadius]);

  return (
    <div ref={containerRef} className={containerClassName}>
      <div
        className={`transition-transform duration-200 ease-out ${className} relative overflow-hidden`}
        style={{
          transform: isDesktop
            ? `perspective(1000px) rotateX(${transform.rotateX}deg) rotateY(${transform.rotateY}deg) translateZ(20px)`
            : "none",
          transformStyle: "preserve-3d",
        }}
      >
        {/* High-res image with zoom animation */}
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `url('${highResImageUrl || imageUrl}')`,
            backgroundSize: highResImageUrl
              ? `${100 + zoomProgress * 200}%` // Zoom from 100% to 300%
              : "cover",
            backgroundPosition: highResImageUrl
              ? `${40 - zoomProgress * 10}% ${65 - panProgress * 20}%` // Pan left and up
              : "center",
            transition: "none",
          }}
        />
      </div>
    </div>
  );
};
